import { AnimatePresence, motion } from 'framer-motion';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Toast } from './Toast';

type ToastType = 'success' | 'warning' | 'error';

interface ToastItem {
  id: string;
  message: string;
  variant: ToastType;
}

interface ToastContextType {
  success: (message: string) => void;
  warning: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  promise: <T>(promise: Promise<T>, options: { loading: string; success: string | ((data: T) => string); error: string | ((error: Error) => string) }) => Promise<T>;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((message: string, variant: ToastType) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, variant }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
  }, []);

  const success = useCallback((message: string) => addToast(message, 'success'), [addToast]);
  const warning = useCallback((message: string) => addToast(message, 'warning'), [addToast]);
  const error = useCallback((message: string) => addToast(message, 'error'), [addToast]);
  const info = useCallback((message: string) => addToast(message, 'success'), [addToast]);

  const promise = useCallback(<T,>(promise: Promise<T>, options: { loading: string; success: string | ((data: T) => string); error: string | ((error: Error) => string) }) => {
    addToast(options.loading, 'warning');

    return promise
      .then((data) => {
        const successMessage = typeof options.success === 'function' ? options.success(data) : options.success;
        addToast(successMessage, 'success');
        return data;
      })
      .catch((error) => {
        const errorMessage = typeof options.error === 'function' ? options.error(error) : options.error;
        addToast(errorMessage, 'error');
        throw error;
      });
  }, [addToast]);

  const contextValue = { success, warning, error, info, promise };

  useEffect(() => {
    setToastContext(contextValue);
  }, [contextValue]);

  return (
    <ToastContext.Provider value={{ success, warning, error, info, promise }}>
      {children}
      <div className="fixed bottom-4 right-4 flex flex-col gap-2" style={{ zIndex: 9999 }}>
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ duration: 0.2, ease: [0.215, 0.61, 0.355, 1] }}
            >
              <Toast
                message={toast.message}
                variant={toast.variant}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

let toastContextValue: ToastContextType | null = null;

export const setToastContext = (context: ToastContextType) => {
  toastContextValue = context;
};

export const toast = {
  success: (message: string) => {
    if (toastContextValue) {
      toastContextValue.success(message);
    }
  },
  warning: (message: string) => {
    if (toastContextValue) {
      toastContextValue.warning(message);
    }
  },
  error: (message: string) => {
    if (toastContextValue) {
      toastContextValue.error(message);
    }
  },
  info: (message: string) => {
    if (toastContextValue) {
      toastContextValue.info(message);
    }
  },
  promise: <T,>(promise: Promise<T>, options: { loading: string; success: string | ((data: T) => string); error: string | ((error: Error) => string) }) => {
    if (toastContextValue) {
      return toastContextValue.promise(promise, options);
    }
    return promise;
  }
};